import { ATTRIBUTION_CAPTURED_AT_KEY, ATTRIBUTION_TTL_MS, type CampaignTouch } from './campaign-touch';

/**
 * First-party cookie mirroring the consented campaign touch so the checkout API can stamp it on
 * Stripe metadata the same way it already reads `_fbc`/`_fbp`. The value is the URL-encoded JSON
 * of a {@link WebCampaignAttribution}.
 */
export const WEB_CAMPAIGN_ATTRIBUTION_COOKIE = 'webCampaignAttribution';

// Ordered by how much a key is worth keeping: when the cookie or the metadata would overflow,
// entries further down the list are the ones dropped. The channel, the ids and the ad hierarchy's
// labels come first; the generic UTMs go last, since `utm_medium` is one constant on paid traffic
// and `utm_campaign` repeats `ad_campaign`. URL campaign labels get an `ad_` prefix so they never
// collide with Adjust web attribution's `campaign`/`adgroup`/`creative`, which checkout spreads into
// the same metadata. Google's click ids come last: Google Ads attributes through its own tag, so
// they only serve a later offline-conversion import.
const METADATA_KEY_BY_CAPTURE_KEY = [
  ['utm_source', 'utm_source'],
  ['campaign_id', 'ad_campaign_id'],
  ['fbclid', 'fbclid'],
  ['ttclid', 'ttclid'],
  ['campaign', 'ad_campaign'],
  ['adgroup_id', 'ad_adgroup_id'],
  ['creative_id', 'ad_creative_id'],
  ['adgroup', 'ad_adgroup'],
  ['creative', 'ad_creative'],
  ['utm_campaign', 'utm_campaign'],
  ['utm_medium', 'utm_medium'],
  ['utm_content', 'utm_content'],
  ['utm_term', 'utm_term'],
  ['gclid', 'gclid'],
  ['gbraid', 'gbraid'],
  ['wbraid', 'wbraid'],
] as const;

export type WebCampaignMetadataKey = (typeof METADATA_KEY_BY_CAPTURE_KEY)[number][1];
export type WebCampaignAttribution = Partial<Record<WebCampaignMetadataKey, string>>;

const METADATA_KEYS = new Set<string>(METADATA_KEY_BY_CAPTURE_KEY.map(([, metadataKey]) => metadataKey));

// Browsers reject a cookie past ~4 KB and Stripe rejects a metadata value past 500 chars; the
// campaign labels Meta expands into the URL are visitor-controlled, so both bounds are enforced here.
const MAX_VALUE_LENGTH = 255;
const MAX_COOKIE_VALUE_LENGTH = 3500;

export const encodeWebCampaignAttribution = (attribution: WebCampaignAttribution): string =>
  encodeURIComponent(JSON.stringify(attribution));

// Stripe rejects the whole request past 50 metadata keys. A fully attributed returning reader already
// spends most of that on product, Adjust, geo and series keys, so campaign keys only fill what is
// left under this ceiling, which reserves a slot for every key Saturn writes onto the same object
// after checkout: two when it creates a student subscription, one when it builds the student
// schedule after payment (that write failing fails the webhook before entitlements are granted),
// one when a renewal discount is redeemed.
export const STRIPE_METADATA_KEY_LIMIT = 50;
export const DOWNSTREAM_METADATA_KEYS = [
  'transitionOfferPriceId',
  'studentScheduleRemainingPeriods',
  'studentScheduleCreated',
  'renewalDiscountRedeemed',
] as const;
export const CAMPAIGN_METADATA_KEY_CEILING = STRIPE_METADATA_KEY_LIMIT - DOWNSTREAM_METADATA_KEYS.length;

/**
 * Adds campaign keys to purchase metadata without ever displacing an existing key or pushing the
 * object past the ceiling; lower-priority campaign keys are the ones left out. `alsoWrittenKeys` are
 * the keys another writer merges into the same Stripe object (e.g. Saturn when it creates a Checkout
 * Session), so the budget covers the complete object, not just this part of it.
 */
export const withCampaignMetadata = <Metadata extends Record<string, unknown>>(
  metadata: Metadata,
  campaign: WebCampaignAttribution,
  { alsoWrittenKeys = [] }: { alsoWrittenKeys?: Iterable<string> } = {},
): Metadata & WebCampaignAttribution => {
  const result: Record<string, unknown> = { ...metadata };
  const occupiedKeys = new Set([...Object.keys(result), ...alsoWrittenKeys]);
  let remaining = CAMPAIGN_METADATA_KEY_CEILING - occupiedKeys.size;

  METADATA_KEY_BY_CAPTURE_KEY.forEach(([, metadataKey]) => {
    const value = campaign[metadataKey];
    if (value === undefined || occupiedKeys.has(metadataKey) || remaining <= 0) {
      return;
    }
    result[metadataKey] = value;
    remaining -= 1;
  });

  return result as Metadata & WebCampaignAttribution;
};

/**
 * The keys Saturn's `StripeService.createCheckoutSession` writes next to the caller's
 * `additionalMetadata`: the product's own metadata plus its base fields. Keep in sync with that method.
 */
export const checkoutSessionBaseMetadataKeys = ({
  productMetadata,
  promoCode,
  hasCoupon,
  hosted,
}: {
  productMetadata?: Record<string, unknown> | null;
  promoCode?: string | null;
  hasCoupon?: boolean;
  hosted?: boolean;
}): string[] => [
  ...Object.keys(productMetadata ?? {}),
  'userId',
  'priceId',
  'productId',
  'language',
  'email',
  'originalPrice',
  ...(promoCode ? ['promoCode'] : []),
  ...(hasCoupon ? ['couponId'] : []),
  ...(hosted ? ['checkoutUi'] : []),
];

/** The campaign subset of a captured touch, capped so it always fits one cookie. */
export const buildWebCampaignAttribution = (attribution: CampaignTouch): WebCampaignAttribution => {
  const result: WebCampaignAttribution = {};

  METADATA_KEY_BY_CAPTURE_KEY.forEach(([captureKey, metadataKey]) => {
    const value = attribution[captureKey];
    if (!value) {
      return;
    }
    const candidate = { ...result, [metadataKey]: value.slice(0, MAX_VALUE_LENGTH) };
    if (encodeWebCampaignAttribution(candidate).length <= MAX_COOKIE_VALUE_LENGTH) {
      result[metadataKey] = candidate[metadataKey];
    }
  });

  return result;
};

const CLICK_ID_KEYS = ['fbclid', 'ttclid', 'gclid', 'gbraid', 'wbraid'] as const;
type WebCampaignClickIdKey = (typeof CLICK_ID_KEYS)[number];
export type WebCampaignLabelKey = Exclude<WebCampaignMetadataKey, WebCampaignClickIdKey>;
export type WebCampaignLabels = Partial<Record<WebCampaignLabelKey, string>>;

const isCampaignLabelKey = (key: WebCampaignMetadataKey): key is WebCampaignLabelKey =>
  !CLICK_ID_KEYS.includes(key as WebCampaignClickIdKey);

/**
 * The campaign labels of a touch as flat properties under the keys checkout stamps on Stripe metadata.
 * Analytics events carry them so a purchase and the funnel steps that led to it read by the same
 * campaign. Built straight from the touch, not through the cookie builder: events have no cookie-size
 * cap, so no label is ever dropped to make room for a click id. Click ids are left out: they identify
 * a person to the ad platform and add nothing to a campaign breakdown.
 */
export const toWebCampaignLabels = (attribution: CampaignTouch): WebCampaignLabels => {
  const labels: WebCampaignLabels = {};
  METADATA_KEY_BY_CAPTURE_KEY.forEach(([captureKey, metadataKey]) => {
    const value = attribution[captureKey];
    if (value && isCampaignLabelKey(metadataKey)) {
      labels[metadataKey] = value.slice(0, MAX_VALUE_LENGTH);
    }
  });
  return labels;
};

/** Keeps only known campaign keys from a parsed cookie, so a tampered cookie cannot inject metadata. */
export const pickWebCampaignAttribution = (record: Record<string, string>): WebCampaignAttribution => {
  const result: WebCampaignAttribution = {};

  Object.entries(record).forEach(([key, value]) => {
    if (METADATA_KEYS.has(key) && typeof value === 'string' && value !== '') {
      result[key as WebCampaignMetadataKey] = value.slice(0, MAX_VALUE_LENGTH);
    }
  });

  return result;
};

const remainingAttributionSeconds = (attribution: CampaignTouch, now: number): number => {
  const capturedAt = Number(attribution[ATTRIBUTION_CAPTURED_AT_KEY]);
  const expiresAt = Number.isFinite(capturedAt) ? capturedAt + ATTRIBUTION_TTL_MS : now + ATTRIBUTION_TTL_MS;
  return Math.max(0, Math.floor((expiresAt - now) / 1000));
};

const cookieAttributes = (): string =>
  `path=/; SameSite=Lax${typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''}`;

/**
 * Mirrors the stored touch into the cookie. The cookie expires with the touch (same TTL clock as
 * the localStorage record) and is removed when the touch carries no campaign keys, so a
 * consent-wiped or expired touch never leaves a stale cookie behind.
 */
export const writeWebCampaignAttributionCookie = (attribution: CampaignTouch, now: number = Date.now()) => {
  if (typeof document === 'undefined') {
    return;
  }
  const campaignAttribution = buildWebCampaignAttribution(attribution);
  if (Object.keys(campaignAttribution).length === 0) {
    clearWebCampaignAttributionCookie();
    return;
  }
  const value = encodeWebCampaignAttribution(campaignAttribution);
  const maxAge = remainingAttributionSeconds(attribution, now);
  document.cookie = `${WEB_CAMPAIGN_ATTRIBUTION_COOKIE}=${value}; max-age=${maxAge}; ${cookieAttributes()}`;
};

export const clearWebCampaignAttributionCookie = () => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${WEB_CAMPAIGN_ATTRIBUTION_COOKIE}=; max-age=0; ${cookieAttributes()}`;
};
